// Resolution of the name shown on a signature block.
//
// Pure and synchronous by design: the print pipeline clones the rendered DOM, so any async
// lookup inside the renderer would race the snapshot capture. Callers load the signer's
// source record up front (DataProvider or fetch) and hand it in.

const USER_TYPES = ['Practitioner', 'Person'];
const CLIENT_TYPES = ['Patient', 'RelatedPerson'];

/**
 * The name to show for a signer, read from their source record.
 * Users get the plain full name; clients get the display name, which carries the
 * preferred name in quotes when one is recorded.
 *
 * @param {object} [resource] fhirworks resource (Practitioner, Person, Patient, RelatedPerson)
 * @returns {string}
 */
export function signerNameFromResource(resource) {
    if (!resource?.resourceType) return '';
    try {
        if (USER_TYPES.includes(resource.resourceType)) return resource.fullName || '';
        if (CLIENT_TYPES.includes(resource.resourceType)) return resource.nameDisplay || '';
    } catch {
        return '';
    }
    return '';
}

/**
 * True when `stored` is an abbreviated form of `resolved` — either a token-wise prefix
 * ending in an initial ('Brittany V' of 'Brittany Vasquez') or an ellipsis-clipped stem
 * ('Brittany Vas…'). Used to decide whether a stored signed name may be healed from the
 * source record.
 *
 * The final token must be a single letter. An abbreviation always ends in one, because it
 * is built from an initial; a rename does not. Without that requirement 'Rowan Underhill'
 * reads as a truncation of a later 'Rowan Underhill-Pace', and a signed name would be
 * silently replaced with one the person never signed under.
 *
 * @param {string} stored
 * @param {string} resolved
 * @returns {boolean}
 */
export function isTruncationOf(stored, resolved) {
    const storedStr = String(stored ?? '').trim();
    const resolvedStr = String(resolved ?? '').trim();
    if (!storedStr || !resolvedStr) return false;
    if (storedStr.toLowerCase() === resolvedStr.toLowerCase()) return false;

    const ellipsis = storedStr.match(/(…|\.\.\.)$/);
    if (ellipsis) {
        const stem = storedStr.slice(0, storedStr.length - ellipsis[0].length).trim();
        return !!stem && resolvedStr.toLowerCase().startsWith(stem.toLowerCase());
    }

    const storedTokens = storedStr.split(/\s+/);
    const resolvedTokens = resolvedStr.split(/\s+/);
    if (storedTokens.length !== resolvedTokens.length) return false;
    if (storedTokens[storedTokens.length - 1].replace(/\.$/, '').length !== 1) return false;

    let anyShorter = false;
    for (let i = 0; i < storedTokens.length; i++) {
        const token = storedTokens[i].replace(/\.$/, '');
        const target = resolvedTokens[i];
        if (!target.toLowerCase().startsWith(token.toLowerCase())) return false;
        if (token.length < target.length) anyShorter = true;
    }
    return anyShorter;
}

/**
 * True when `stored` is the calendar day-view abbreviation of this person's name —
 * first name plus the initial of the family name. The family name can be multi-word, so
 * this shape does not survive a token-wise comparison and needs its own check.
 *
 * @param {string} stored
 * @param {object} [resource] The signer's source resource
 * @returns {boolean}
 */
export function isCalendarAbbreviationOf(stored, resource) {
    const storedStr = String(stored ?? '')
        .trim()
        .replace(/\.$/, '');
    if (!storedStr || !resource) return false;

    let firstName = '';
    let lastName = '';
    try {
        firstName = resource.firstName || '';
        lastName = resource.lastName || '';
    } catch {
        return false;
    }
    if (!firstName || !lastName) return false;

    return storedStr.toLowerCase() === `${firstName} ${lastName[0]}`.toLowerCase();
}

/**
 * True when the reviewer's audit `user` is provably the signer themselves.
 *
 * `user` records who was logged in when the signature was captured, which is not always
 * the signer — a client can sign in person on a staff member's device. Reading
 * `user.display` is therefore only safe when the signer is a user and the ids match.
 * Client signers are excluded even on an id match: `user.display` is the plain full name
 * and cannot carry the preferred name a client signature shows.
 *
 * @param {object} reviewer QuestionnaireResponse.reviewer entry
 * @returns {boolean}
 */
export function isUserSignerMatch(reviewer) {
    const who = reviewer?.who;
    const user = reviewer?.user;
    if (!who || !user) return false;
    if (!USER_TYPES.includes(who.resourceType)) return false;
    if (!user.id || user.id !== who.id) return false;
    return !!user.display;
}

/**
 * The name to render for one signer/reviewer entry.
 *
 * Pending signatures track the source record's current name. A signed signature is frozen
 * at the name captured when it was signed; the only rewrite is healing a stored name that
 * is an abbreviation of the source record's name.
 *
 * @param {object} reviewer QuestionnaireResponse.reviewer entry
 * @param {object} [sourceRecord] The signer's source resource, when loaded
 * @returns {string}
 */
export function resolveSignerDisplay(reviewer, sourceRecord) {
    const who = reviewer?.who;
    if (!who) return '';

    const stored = who.display || '';
    const fallback = stored || who.fullName || '';
    const resolved = signerNameFromResource(sourceRecord);

    // Pending — follow the source record.
    if (!reviewer.when) return resolved || fallback;

    // Signed by the user themselves — the name as of sign time is authoritative.
    if (isUserSignerMatch(reviewer)) return reviewer.user.display;

    if (!resolved) return fallback;
    if (!stored) return resolved;
    if (isTruncationOf(stored, resolved) || isCalendarAbbreviationOf(stored, sourceRecord)) return resolved;
    return stored;
}

/**
 * Pick the source record matching a signer reference out of a loaded collection.
 * Callers hold their records in different places — a DataProvider ref, a prop, a plain
 * array — so this takes the collection explicitly rather than reaching for one.
 *
 * @param {object} who reviewer.who
 * @param {Array} sourceRecords loaded Practitioner/Patient/Person/RelatedPerson resources
 * @returns {object|undefined}
 */
export function findSignerSource(who, sourceRecords) {
    return (sourceRecords || []).find((record) => record?.id === who?.id && record?.resourceType === who?.resourceType);
}
